
import { useEffect, useState, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";

interface CurrentUser {
  id: string;
  email: string;
  full_name: string;
  avatar_url?: string;
  user_type: 'tenant' | 'landlord' | 'admin' | 'service_provider';
  is_active?: boolean;
  is_verified?: boolean;
}

export const useCurrentUser = () => {
  const [currentUser, setCurrentUser] = useState<CurrentUser | null>(null);
  const [loading, setLoading] = useState(true);
  const isFetching = useRef(false);

  useEffect(() => {
    const fetchCurrentUser = async () => {
      // منع الاستدعاءات المتكررة
      if (isFetching.current) return;
      isFetching.current = true;

      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          setCurrentUser(null);
          setLoading(false);
          isFetching.current = false;
          return;
        }

        // محاولة تفعيل الحساب إذا تم تأكيد البريد (فقط مرة واحدة)
        try {
          await supabase.rpc('activate_account_after_email_verification', {
            p_user_id: user.id
          });
        } catch (activationError) {
          // تجاهل الأخطاء - قد لا يكون الحساب بحاجة للتفعيل
        }

        let actualUserType: 'tenant' | 'landlord' | 'admin' | 'service_provider' = 'tenant';
        let userData: any = null;
        let isActive = false;
        let isVerified = false;

        // التحقق من كونه مشرف في kanfadmin
        const { data: kanfAdminData } = await supabase
          .from('kanfadmin')
          .select('full_name, email, is_active')
          .eq('email', user.email)
          .maybeSingle();

        if (kanfAdminData) {
          if (!kanfAdminData.is_active) {
            // حساب المشرف معطل - تسجيل الخروج
            await supabase.auth.signOut();
            setCurrentUser(null);
            setLoading(false);
            isFetching.current = false;
            return;
          }
          actualUserType = 'admin';
          userData = kanfAdminData;
          isActive = true;
          isVerified = true;
        } else {
          // التحقق من جدول المشرفين العاديين
          const { data: adminData } = await supabase
            .from('admins')
            .select('full_name, avatar_url, is_active')
            .eq('user_id', user.id)
            .maybeSingle();

          if (adminData) {
            if (!adminData.is_active) {
              await supabase.auth.signOut();
              setCurrentUser(null);
              setLoading(false);
              isFetching.current = false;
              return;
            }
            actualUserType = 'admin';
            userData = adminData;
            isActive = true;
            isVerified = true;
          } else {
            // التحقق من جدول أصحاب الخدمات
            const { data: serviceProviderData } = await supabase
              .from('service_providers')
              .select('full_name, company_logo_url, is_active, is_verified')
              .eq('user_id', user.id)
              .maybeSingle();

            if (serviceProviderData) {
              // السماح بالدخول حتى لو لم يكن مفعلاً - فقط لعرض لوحة التحكم
              actualUserType = 'service_provider';
              userData = { 
                ...serviceProviderData, 
                avatar_url: serviceProviderData.company_logo_url 
              };
              isActive = serviceProviderData.is_active || false;
              isVerified = serviceProviderData.is_verified || false;
            } else {
              // التحقق من جدول الملاك
              const { data: landlordData } = await supabase
                .from('landlords')
                .select('full_name, avatar_url, is_active, is_verified')
                .eq('user_id', user.id)
                .maybeSingle();

              if (landlordData) {
                // السماح بالدخول حتى لو لم يكن مفعلاً - فقط لعرض لوحة التحكم
                actualUserType = 'landlord';
                userData = landlordData;
                isActive = landlordData.is_active || false;
                isVerified = landlordData.is_verified || false;
              } else {
                // التحقق من جدول الباحثين
                const { data: tenantData } = await supabase
                  .from('tenants')
                  .select('full_name, avatar_url, is_active')
                  .eq('user_id', user.id)
                  .maybeSingle();

                if (tenantData) {
                  if (!tenantData.is_active) {
                    await supabase.auth.signOut();
                    setCurrentUser(null);
                    setLoading(false);
                    isFetching.current = false;
                    return;
                  }
                  actualUserType = 'tenant';
                  userData = tenantData;
                  isActive = true;
                  isVerified = true;
                }
              }
            }
          }
        }

        // تعيين بيانات المستخدم
        setCurrentUser({
          id: user.id,
          email: user.email || '',
          full_name: userData?.full_name || user.user_metadata?.full_name || '',
          avatar_url: userData?.avatar_url || '',
          user_type: actualUserType,
          is_active: isActive,
          is_verified: isVerified
        });

        // تحديث metadata للمستخدم إذا كان مختلفاً
        if (user.user_metadata?.user_type !== actualUserType) {
          setTimeout(() => {
            supabase.auth.updateUser({
              data: { 
                ...user.user_metadata,
                user_type: actualUserType 
              }
            });
          }, 0);
        }
      } catch (error) {
        setCurrentUser(null);
      }
      setLoading(false);
      isFetching.current = false;
    };

    fetchCurrentUser();

    // الاستماع لتغييرات المصادقة
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT') {
        setCurrentUser(null);
        setLoading(false);
        isFetching.current = false;
      } else if (event === 'SIGNED_IN' && session?.user) {
        // تأجيل fetchCurrentUser لتجنب infinite loop
        setTimeout(() => {
          isFetching.current = false;
          fetchCurrentUser();
        }, 100);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  return { currentUser, loading };
};
